import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AppleCallback({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        console.log('Apple callback - location:', location);
        console.log('Apple callback - search params:', location.search);
        console.log('Apple callback - hash:', location.hash);
        
        // Try to get data from URL parameters (query string)
        const params = new URLSearchParams(location.search);
        let code = params.get('code');
        let idToken = params.get('id_token');
        let state = params.get('state');
        let userParam = params.get('user');
        
        // If not in query string, try hash fragment
        if (!code && location.hash) {
          const hashParams = new URLSearchParams(location.hash.substring(1));
          code = hashParams.get('code');
          idToken = hashParams.get('id_token');
          state = hashParams.get('state');
          userParam = hashParams.get('user');
        }

        console.log('Apple callback - parsed data:', { code, idToken, state, userParam });

        if (!code && !idToken) {
          throw new Error('No authorization code or ID token received from Apple. Please try again.');
        }

        let userData = null;
        if (userParam) {
          try {
            userData = JSON.parse(userParam);
          } catch (e) {
            console.warn('Could not parse user data from Apple:', e);
          }
        }

        console.log('Sending to backend:', { code, id_token: idToken, user: userData });

        const response = await axios.post(`${API}/auth/apple/callback`, {
          code: code || idToken,
          id_token: idToken,
          user: userData
        });

        console.log('Backend response:', response.data);

        if (response.data.token) {
          toast.success('Signed in with Apple successfully!');
          onLogin(response.data.token);
          
          // Small delay to ensure token is saved
          setTimeout(() => {
            navigate('/onboarding', { state: { user: response.data.user } });
          }, 100);
        } else {
          throw new Error('No token received from backend');
        }
      } catch (error) {
        console.error('Apple auth error:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Apple authentication failed';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // Redirect to auth page after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      }
    };

    handleCallback();
  }, [location, navigate, onLogin]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
            Apple Sign In Failed
          </h2>
          <p className="text-muted-foreground mb-4">
            {error}
          </p>
          <p className="text-sm text-muted-foreground">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
          Completing Apple Sign In...
        </h2>
        <p className="text-muted-foreground">
          Please wait while we authenticate your account
        </p>
      </div>
    </div>
  );
}
